export const ARCHIVE_EVENT_TYPES = Object.freeze([
  'communication',
  'approval',
  'delivery',
  'financial',
  'risk',
  'gate',
  'document',
  'stakeholder',
  'system',
]);

export const ARCHIVE_SOURCE_TYPES = Object.freeze([
  'email',
  'meeting',
  'ado',
  'document',
  'gate',
  'finance',
  'manual',
]);

export const ARCHIVE_APPROVAL_STATUSES = Object.freeze([
  'approved',
  'pending-review',
  'rejected',
  'not-required',
]);

export const ARCHIVE_TIME_WINDOWS = Object.freeze([
  { key: 'last-week', label: 'Last week' },
  { key: 'last-30-days', label: 'Last 30 days' },
  { key: 'last-pi', label: 'Last PI' },
]);

const SENTINEL_GUARD_EVENTS = Object.freeze([
  {
    id: 'archive-sg-customer-email-approval',
    projectId: 'PRJ-9924-A',
    occurredAt: '2026-05-13T14:25:00Z',
    title: 'Customer email approval captured for pilot readiness scope',
    summary:
      'The product owner confirmed the pilot readiness scope with one condition: identity integration status must be reviewed before the Gate 3 decision forum.',
    eventType: 'approval',
    sourceType: 'email',
    actor: 'Product owner',
    organization: 'Sentinel Guard PMO',
    approvalStatus: 'approved',
    versionLabel: 'v1.3 decision package',
    impact: 'high',
    confidence: 0.92,
    summaryWindows: ['last-week', 'last-30-days', 'last-pi'],
    citations: [
      {
        label: 'Customer email approval',
        excerpt:
          'Pilot readiness scope approved pending identity integration review before Gate 3.',
        sourceType: 'email',
      },
    ],
  },
  {
    id: 'archive-sg-pll-timeline-review',
    projectId: 'PRJ-9924-A',
    occurredAt: '2026-05-12T18:10:00Z',
    title: 'Timeline update marked for PLL review',
    summary:
      'The final integration dry run moved by two business days, requiring Robin Russell to review the timeline exception before the Gate 3 readiness brief is finalized.',
    eventType: 'delivery',
    sourceType: 'meeting',
    actor: 'Maya Chen',
    organization: 'Sentinel Guard PMO',
    approvalStatus: 'pending-review',
    versionLabel: 'v1.3 milestone deck',
    impact: 'high',
    confidence: 0.88,
    summaryWindows: ['last-week', 'last-30-days', 'last-pi'],
    citations: [
      {
        label: 'PLL timeline note',
        excerpt: 'PLL review requested for the two-day integration dry-run movement before Gate 3.',
        sourceType: 'meeting',
      },
    ],
  },
  {
    id: 'archive-sg-gate-evidence',
    projectId: 'PRJ-9924-A',
    occurredAt: '2026-05-10T15:40:00Z',
    title: 'Gate decision evidence packet reached quorum',
    summary:
      'Gate 3 criteria, cyber evidence, cost variance, and field pilot scope were packaged into the decision record with all required evidence references visible.',
    eventType: 'gate',
    sourceType: 'gate',
    actor: 'Robin Russell',
    organization: 'Portfolio leadership layer',
    approvalStatus: 'approved',
    versionLabel: 'v1.2 gate packet',
    impact: 'high',
    confidence: 0.9,
    summaryWindows: ['last-week', 'last-30-days', 'last-pi'],
    citations: [
      {
        label: 'Gate 3 evidence index',
        excerpt:
          'Decision packet includes cyber control trace, forecast delta, pilot scope, and delivery exception notes.',
        sourceType: 'gate',
      },
    ],
  },
  {
    id: 'archive-sg-ado-delivery-movement',
    projectId: 'PRJ-9924-A',
    occurredAt: '2026-05-09T20:30:00Z',
    title: 'ADO delivery movement closed integration blocker',
    summary:
      'The delivery board moved the identity adapter blocker to done after test evidence was attached, clearing the next Sentinel Guard integration lane.',
    eventType: 'delivery',
    sourceType: 'ado',
    actor: 'Engineering support cell',
    organization: 'Technical delivery',
    approvalStatus: 'not-required',
    versionLabel: 'Sprint 26.10 board',
    impact: 'medium',
    confidence: 0.86,
    summaryWindows: ['last-week', 'last-30-days', 'last-pi'],
    citations: [
      {
        label: 'ADO work item movement',
        excerpt: 'Identity adapter blocker moved to done with test evidence attached.',
        sourceType: 'ado',
      },
    ],
  },
  {
    id: 'archive-sg-financial-forecast-note',
    projectId: 'PRJ-9924-A',
    occurredAt: '2026-05-08T13:05:00Z',
    title: 'Financial forecast adjusted for pilot environment support',
    summary:
      'Finance added a small forecast adjustment for pilot environment support while keeping the total variance inside the executive watch threshold.',
    eventType: 'financial',
    sourceType: 'finance',
    actor: 'Resource manager',
    organization: 'PEO DHMS Contracting Cell',
    approvalStatus: 'pending-review',
    versionLabel: 'v1.1 forecast workbook',
    impact: 'medium',
    confidence: 0.84,
    summaryWindows: ['last-week', 'last-30-days', 'last-pi'],
    citations: [
      {
        label: 'Forecast adjustment note',
        excerpt:
          'Pilot environment support added to forecast; variance remains inside executive watch threshold.',
        sourceType: 'finance',
      },
    ],
  },
  {
    id: 'archive-sg-risk-register-update',
    projectId: 'PRJ-9924-A',
    occurredAt: '2026-05-06T21:15:00Z',
    title: 'Risk register updated for identity dependency',
    summary:
      'The identity integration dependency remained open, but mitigation ownership moved to the cyber integration office with a dated follow-up before Gate 3.',
    eventType: 'risk',
    sourceType: 'manual',
    actor: 'DHA Cyber Integration Office',
    organization: 'Cyber assurance',
    approvalStatus: 'not-required',
    versionLabel: 'v1.0 risk register',
    impact: 'high',
    confidence: 0.89,
    summaryWindows: ['last-week', 'last-30-days', 'last-pi'],
    citations: [
      {
        label: 'Risk register entry',
        excerpt:
          'Identity integration dependency remains open; cyber office owns Gate 3 mitigation follow-up.',
        sourceType: 'manual',
      },
    ],
  },
  {
    id: 'archive-sg-raci-change',
    projectId: 'PRJ-9924-A',
    occurredAt: '2026-05-03T16:45:00Z',
    title: 'Stakeholder RACI change added EIDS data steward consultation',
    summary:
      'The RACI map added the EIDS Data Steward Team as consulted for source lineage and evidence traceability during the Gate 3 review window.',
    eventType: 'stakeholder',
    sourceType: 'meeting',
    actor: 'Maya Chen',
    organization: 'Sentinel Guard PMO',
    approvalStatus: 'approved',
    versionLabel: 'v1.0 RACI map',
    impact: 'medium',
    confidence: 0.87,
    summaryWindows: ['last-30-days', 'last-pi'],
    citations: [
      {
        label: 'Stakeholder review minutes',
        excerpt:
          'EIDS Data Steward Team added as consulted for source lineage and evidence traceability.',
        sourceType: 'meeting',
      },
    ],
  },
  {
    id: 'archive-sg-document-routing-status',
    projectId: 'PRJ-9924-A',
    occurredAt: '2026-04-29T19:00:00Z',
    title: 'Document routing status marked decision-package ready',
    summary:
      'The briefing and artifact bundle moved to decision-package ready after the cyber control evidence and pilot-scope attachment were reconciled.',
    eventType: 'document',
    sourceType: 'document',
    actor: 'Briefing coordinator',
    organization: 'Sentinel Guard PMO',
    approvalStatus: 'approved',
    versionLabel: 'v1.0 briefing bundle',
    impact: 'medium',
    confidence: 0.83,
    summaryWindows: ['last-30-days', 'last-pi'],
    citations: [
      {
        label: 'Routing status memo',
        excerpt:
          'Decision-package ready after cyber control evidence and pilot-scope attachment reconciliation.',
        sourceType: 'document',
      },
    ],
  },
  {
    id: 'archive-sg-system-sync',
    projectId: 'PRJ-9924-A',
    occurredAt: '2026-04-26T11:20:00Z',
    title: 'System source sync refreshed delivery and finance cues',
    summary:
      'The archive recorded a source sync that refreshed delivery movement, forecast notes, and gate evidence references for the Sentinel Guard profile.',
    eventType: 'system',
    sourceType: 'manual',
    actor: 'Data steward',
    organization: 'DHA PRIME operations',
    approvalStatus: 'not-required',
    versionLabel: 'Archive snapshot 2026.04.26',
    impact: 'low',
    confidence: 0.8,
    summaryWindows: ['last-30-days', 'last-pi'],
    citations: [
      {
        label: 'Sync manifest',
        excerpt:
          'Delivery, finance, and gate evidence references refreshed for the Sentinel Guard profile.',
        sourceType: 'manual',
      },
    ],
  },
  {
    id: 'archive-sg-product-owner-thread',
    projectId: 'PRJ-9924-A',
    occurredAt: '2026-04-22T14:45:00Z',
    title: 'Product owner thread clarified field pilot acceptance',
    summary:
      'The product owner clarified that field pilot acceptance depends on operator signoff plus the identity integration review record, not a new scope change.',
    eventType: 'communication',
    sourceType: 'email',
    actor: 'Product owner',
    organization: 'Sentinel Guard PMO',
    approvalStatus: 'not-required',
    versionLabel: 'v0.9 pilot thread',
    impact: 'medium',
    confidence: 0.81,
    summaryWindows: ['last-30-days', 'last-pi'],
    citations: [
      {
        label: 'Pilot acceptance email',
        excerpt:
          'Acceptance depends on operator signoff and identity review record; no new scope change requested.',
        sourceType: 'email',
      },
    ],
  },
  {
    id: 'archive-sg-cyber-control-document',
    projectId: 'PRJ-9924-A',
    occurredAt: '2026-04-15T17:10:00Z',
    title: 'Cyber control document added to evidence archive',
    summary:
      'The cyber assurance team attached the control evidence excerpt used by the Gate 3 packet and marked the source as read-only for review.',
    eventType: 'document',
    sourceType: 'document',
    actor: 'DHA Cyber Integration Office',
    organization: 'Cyber assurance',
    approvalStatus: 'approved',
    versionLabel: 'v0.8 control evidence',
    impact: 'high',
    confidence: 0.85,
    summaryWindows: ['last-30-days', 'last-pi'],
    citations: [
      {
        label: 'Cyber control excerpt',
        excerpt: 'Control evidence attached to Gate 3 packet and marked read-only for review.',
        sourceType: 'document',
      },
    ],
  },
  {
    id: 'archive-sg-pi-planning-baseline',
    projectId: 'PRJ-9924-A',
    occurredAt: '2026-03-28T15:00:00Z',
    title: 'PI planning baseline established Gate 3 success measures',
    summary:
      'The PI planning record established Gate 3 success measures for pilot readiness, decision evidence, financial variance, and stakeholder signoff.',
    eventType: 'gate',
    sourceType: 'meeting',
    actor: 'Portfolio leadership layer',
    organization: 'DHA PRIME operations',
    approvalStatus: 'approved',
    versionLabel: 'PI-26.2 baseline',
    impact: 'high',
    confidence: 0.82,
    summaryWindows: ['last-pi'],
    citations: [
      {
        label: 'PI planning baseline',
        excerpt:
          'Gate 3 success measures include pilot readiness, decision evidence, variance control, and signoff.',
        sourceType: 'meeting',
      },
    ],
  },
]);

function fallbackEventsFor(project) {
  const projectId = project.id || 'project';
  const projectName = project.name || 'Project';

  return [
    {
      id: `${projectId}-archive-leadership-summary`,
      projectId,
      occurredAt: '2026-05-13T12:00:00Z',
      title: `${projectName} leadership summary refreshed`,
      summary: `${projectName} received a narrative summary that connects delivery posture, decision readiness, and near-term owner follow-up for review.`,
      eventType: 'communication',
      sourceType: 'meeting',
      actor: 'Project manager',
      organization: 'Program delivery office',
      approvalStatus: 'not-required',
      versionLabel: 'v1.0 summary',
      impact: 'medium',
      confidence: 0.8,
      summaryWindows: ['last-week', 'last-30-days', 'last-pi'],
      citations: [
        {
          label: `${projectName} leadership note`,
          excerpt:
            'Leadership summary ties delivery posture to decision readiness and owner follow-up.',
          sourceType: 'meeting',
        },
      ],
    },
    {
      id: `${projectId}-archive-delivery-update`,
      projectId,
      occurredAt: '2026-05-09T16:30:00Z',
      title: `${projectName} delivery update captured`,
      summary: `${projectName} delivery work moved through the archive with current milestone status, dependency notes, and visible evidence references.`,
      eventType: 'delivery',
      sourceType: 'ado',
      actor: 'Delivery lead',
      organization: 'Execution team',
      approvalStatus: 'not-required',
      versionLabel: 'Sprint board snapshot',
      impact: 'medium',
      confidence: 0.78,
      summaryWindows: ['last-week', 'last-30-days', 'last-pi'],
      citations: [
        {
          label: `${projectName} board note`,
          excerpt:
            'Milestone status, dependency notes, and evidence references are available in the archive.',
          sourceType: 'ado',
        },
      ],
    },
    {
      id: `${projectId}-archive-approval-cue`,
      projectId,
      occurredAt: '2026-05-01T13:15:00Z',
      title: `${projectName} approval cue retained as read-only`,
      summary: `${projectName} keeps the approval posture visible as a display-only archive cue so reviewers can understand the running product history without changing workflow state.`,
      eventType: 'approval',
      sourceType: 'document',
      actor: 'Product owner',
      organization: 'Product governance',
      approvalStatus: 'approved',
      versionLabel: 'v0.9 review packet',
      impact: 'medium',
      confidence: 0.79,
      summaryWindows: ['last-30-days', 'last-pi'],
      citations: [
        {
          label: `${projectName} approval cue`,
          excerpt: 'Approval posture is preserved as display-only context for the archive.',
          sourceType: 'document',
        },
      ],
    },
    {
      id: `${projectId}-archive-risk-note`,
      projectId,
      occurredAt: '2026-04-18T18:45:00Z',
      title: `${projectName} risk note linked to owner follow-up`,
      summary: `${projectName} includes a risk-register summary with owner, mitigation posture, and citation text that does not imply live source-system retrieval.`,
      eventType: 'risk',
      sourceType: 'manual',
      actor: 'Risk owner',
      organization: 'Program delivery office',
      approvalStatus: 'pending-review',
      versionLabel: 'v0.8 risk note',
      impact: 'high',
      confidence: 0.76,
      summaryWindows: ['last-30-days', 'last-pi'],
      citations: [
        {
          label: `${projectName} risk note`,
          excerpt:
            'Owner and mitigation posture captured as illustrative history with source excerpt text.',
          sourceType: 'manual',
        },
      ],
    },
    {
      id: `${projectId}-archive-pi-baseline`,
      projectId,
      occurredAt: '2026-03-29T15:20:00Z',
      title: `${projectName} PI baseline preserved`,
      summary: `${projectName} preserves the PI baseline as corporate memory so the current summary can be read against earlier scope, version, and decision expectations.`,
      eventType: 'gate',
      sourceType: 'meeting',
      actor: 'Portfolio manager',
      organization: 'Portfolio office',
      approvalStatus: 'approved',
      versionLabel: 'PI baseline',
      impact: 'medium',
      confidence: 0.77,
      summaryWindows: ['last-pi'],
      citations: [
        {
          label: `${projectName} PI baseline`,
          excerpt:
            'Baseline scope, version, and decision expectations preserved for current-state comparison.',
          sourceType: 'meeting',
        },
      ],
    },
  ];
}

function cloneEvents(events) {
  return events.map((event) => ({
    ...event,
    summaryWindows: [...event.summaryWindows],
    citations: event.citations.map((citation) => ({ ...citation })),
  }));
}

function normalizeFilter(value) {
  const normalized = String(value || '').trim();
  return normalized && normalized !== 'all' ? normalized : null;
}

function searchableEventText(event) {
  return [
    event.title,
    event.summary,
    event.actor,
    event.organization,
    event.versionLabel,
    event.eventType,
    event.sourceType,
    event.approvalStatus,
    ...(event.citations || []).flatMap((citation) => [
      citation.label,
      citation.excerpt,
      citation.sourceType,
    ]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function getProjectArchiveDemoData(project = {}) {
  const projectId = project.id || 'project';
  const projectName = project.name || 'Project';
  const isSentinelGuard = projectId === 'PRJ-9924-A';

  return {
    projectId,
    projectName,
    generatedAt: '2026-05-14T16:00:00Z',
    posture: 'Illustrative data',
    readOnly: true,
    events: cloneEvents(isSentinelGuard ? SENTINEL_GUARD_EVENTS : fallbackEventsFor(project)),
  };
}

export function filterProjectArchiveEvents(events, filters = {}) {
  const query = normalizeFilter(filters.query || filters.text);
  const eventType = normalizeFilter(filters.eventType);
  const sourceType = normalizeFilter(filters.sourceType);
  const approvalStatus = normalizeFilter(filters.approvalStatus);
  const timeWindow = normalizeFilter(filters.timeWindow);
  const queryText = query?.toLowerCase();

  return [...(events || [])]
    .filter((event) => {
      if (queryText && !searchableEventText(event).includes(queryText)) return false;
      if (eventType && event.eventType !== eventType) return false;
      if (sourceType && event.sourceType !== sourceType) return false;
      if (approvalStatus && event.approvalStatus !== approvalStatus) return false;
      if (timeWindow && !event.summaryWindows.includes(timeWindow)) return false;
      return true;
    })
    .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());
}

export function summarizeProjectArchiveWindows(events) {
  return ARCHIVE_TIME_WINDOWS.map((window) => {
    const windowEvents = filterProjectArchiveEvents(events, { timeWindow: window.key });
    const eventTypeCounts = windowEvents.reduce((counts, event) => {
      counts[event.eventType] = (counts[event.eventType] || 0) + 1;
      return counts;
    }, {});
    const dominantEventTypes = Object.entries(eventTypeCounts)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, 2)
      .map(([type]) => type);
    const latestWithApproval = windowEvents.find(
      (event) => event.approvalStatus && event.approvalStatus !== 'not-required',
    );
    const latestWithVersion = windowEvents.find((event) => event.versionLabel);

    return {
      key: window.key,
      label: window.label,
      eventCount: windowEvents.length,
      dominantEventTypes,
      highlight: windowEvents[0]?.title || 'No archive events in this window',
      approvalCue: latestWithApproval?.approvalStatus || 'not-required',
      versionCue: latestWithVersion?.versionLabel || 'No version cue',
    };
  });
}
